import React from 'react';
import { Button } from './ui/Button';
import { ArrowRight, CheckCircle2, Star, Shield, Sparkles } from 'lucide-react';
import { AnimatedGridPattern } from './ui/AnimatedGridPattern';
interface HeroSectionProps {
  title: string;
  subtitle: string;
  primaryCtaText: string;
  secondaryCtaText: string;
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
  benefits?: string[];
}
export function HeroSection({
  title,
  subtitle,
  primaryCtaText,
  secondaryCtaText,
  onPrimaryClick,
  onSecondaryClick,
  benefits = []
}: HeroSectionProps) {
  return <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 pt-28 pb-32 lg:pt-44 lg:pb-48 transition-colors duration-300" data-id="element-196">
      {/* Animated Grid Pattern Background */}
      <AnimatedGridPattern numSquares={30} maxOpacity={0.15} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-197" />

      {/* Gradient Overlay for depth */}
      <div className="absolute inset-0 -z-5" data-id="element-198">
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-transparent to-white/80 dark:from-slate-950/60 dark:to-slate-950/80" data-id="element-199"></div>
      </div>

      {/* Premium Animated Background Orbs */}
      <div className="absolute inset-0 -z-10" data-id="element-200">
        <div className="absolute top-20 right-1/4 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-blue-400/10 to-sky-400/10 dark:from-blue-500/5 dark:to-sky-500/5 blur-3xl animate-float" data-id="element-201"></div>
        <div className="absolute bottom-20 left-1/4 h-[450px] w-[450px] rounded-full bg-gradient-to-tr from-sky-400/10 to-blue-400/10 dark:from-sky-500/5 dark:to-blue-500/5 blur-3xl animate-float" style={{
        animationDelay: '5s',
        animationDuration: '25s'
      }} data-id="element-202"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[400px] rounded-full bg-gradient-to-r from-cyan-400/8 to-blue-400/8 dark:from-cyan-500/5 dark:to-blue-500/5 blur-3xl animate-float" style={{
        animationDelay: '10s',
        animationDuration: '30s'
      }} data-id="element-203"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-204">
        {/* Centered Content */}
        <div className="max-w-5xl mx-auto text-center" data-id="element-205">
          <div className="flex flex-col items-center space-y-10" data-id="element-206">
            {/* Premium Trust Badge */}
            <div className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-white/90 to-white/70 dark:from-slate-800/90 dark:to-slate-800/70 backdrop-blur-md px-5 py-2.5 text-xs sm:text-sm font-semibold text-blue-700 dark:text-blue-300 border border-blue-200/50 dark:border-blue-700/30 shadow-lg shadow-blue-500/10 dark:shadow-blue-900/10 ring-1 ring-white/40 dark:ring-white/10" data-id="element-207">
              <div className="relative" data-id="element-208">
                <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-209" />
                <div className="absolute inset-0 bg-blue-400 rounded-full blur-sm opacity-30 animate-pulse" data-id="element-210"></div>
              </div>
              <span className="leading-tight bg-gradient-to-r from-blue-700 to-blue-500 dark:from-blue-300 dark:to-sky-300 bg-clip-text text-transparent" data-id="element-211">
                Deutschlands führende Versicherungsplattform
              </span>
              <Sparkles className="h-3.5 w-3.5 text-amber-500 animate-pulse" data-id="element-212" />
            </div>

            {/* Premium Title with Enhanced Typography */}
            <div className="space-y-8" data-id="element-213">
              <h1 className="text-5xl sm:text-6xl md:text-7xl xl:text-8xl font-extrabold tracking-tight leading-[1.1]" data-id="element-214">
                <span className="text-slate-900 dark:text-white drop-shadow-sm" data-id="element-215">
                  {title.split('. ')[0]}.
                </span>
                <br data-id="element-216" />
                <span className="relative inline-block mt-2" data-id="element-217">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-sky-500 dark:from-blue-400 dark:via-blue-300 dark:to-sky-400 animate-gradient bg-[length:200%_auto]" data-id="element-218">
                    {title.split('. ')[1]}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-blue-500/20 to-sky-500/20 blur-2xl -z-10" data-id="element-219"></div>
                </span>
              </h1>
              <p className="max-w-3xl mx-auto text-xl sm:text-2xl md:text-3xl text-slate-600 dark:text-slate-300 leading-relaxed font-light" data-id="element-220">
                {subtitle}
              </p>
            </div>

            {/* Premium Benefits with Enhanced Icons */}
            {benefits.length > 0 && <ul className="flex flex-col items-center space-y-4 max-w-lg mx-auto" data-id="element-221">
                {benefits.map((benefit, index) => <li key={index} className="flex items-center gap-3.5 text-base sm:text-lg text-slate-700 dark:text-slate-300 font-medium group" data-id="element-222">
                    <div className="relative" data-id="element-223">
                      <CheckCircle2 className="h-6 w-6 text-green-500 dark:text-green-400 flex-shrink-0 transition-transform group-hover:scale-110" data-id="element-224" />
                      <div className="absolute inset-0 bg-green-400 rounded-full blur-md opacity-0 group-hover:opacity-40 transition-opacity" data-id="element-225"></div>
                    </div>
                    <span className="transition-colors group-hover:text-slate-900 dark:group-hover:text-white" data-id="element-226">
                      {benefit}
                    </span>
                  </li>)}
              </ul>}

            {/* Premium CTAs with Fixed Sizes and Gradient Border */}
            <div className="flex flex-col gap-4 sm:gap-6 sm:flex-row pt-6 justify-center items-center" data-id="element-227">
              {/* Primary Button with Gradient Border Wrapper */}
              <a href="https://www.maklerinfo.biz/simplr/register/00R7RW/" target="_blank" rel="noopener noreferrer" className="group p-[1px] rounded-xl bg-gradient-to-r from-blue-600 via-blue-400 to-sky-500 shadow-lg shadow-blue-500/20 w-full sm:w-auto" data-id="element-228">
                <span className="inline-flex items-center justify-center gap-2.5 text-base font-semibold px-8 h-12 w-full transition-all duration-300 group-hover:scale-[1.02] group-active:scale-[0.98] rounded-[11px] bg-gradient-to-r from-blue-600 to-blue-500 text-white group-hover:from-blue-700 group-hover:to-blue-600 shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/50" data-id="element-229">
                  <span className="flex items-center gap-2" data-id="element-230">
                    {primaryCtaText}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-231" />
                  </span>
                </span>
              </a>

              {/* Secondary Button with Fixed Text Color */}
              <Button size="lg" variant="outline" onClick={onSecondaryClick} className="text-base font-medium px-8 h-12 w-full sm:w-auto border-2 border-slate-200 hover:border-blue-300 text-slate-700 dark:text-white dark:border-slate-700 dark:hover:bg-slate-800/70 hover:bg-blue-50/50 backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-md rounded-xl" data-id="element-232">
                {secondaryCtaText}
              </Button>
            </div>

            {/* Premium Social Proof with Enhanced Design */}
            <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8 text-sm text-slate-500 dark:text-slate-400 pt-12 border-t border-slate-200/60 dark:border-slate-800/60 w-full max-w-lg justify-center" data-id="element-233">
              <div className="flex -space-x-3" data-id="element-234">
                {['https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/68.jpg', 'https://randomuser.me/api/portraits/men/75.jpg'].map((src, i) => <img key={i} src={src} alt={`Kunde ${i + 1}`} className="h-10 w-10 rounded-full border-2 border-white dark:border-slate-900 shadow-lg hover:scale-110 hover:z-10 transition-transform cursor-pointer ring-2 ring-white/50 dark:ring-white/10 object-cover" data-id="element-235" />)}
              </div>
              <div className="flex flex-col items-center sm:items-start" data-id="element-236">
                <div className="flex items-center gap-2.5" data-id="element-237">
                  <div className="flex" data-id="element-238">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400 drop-shadow-sm" data-id="element-239" />)}
                  </div>
                  <span className="font-bold text-slate-900 dark:text-white text-base" data-id="element-240">
                    4.9/5
                  </span>
                </div>
                <p className="text-sm sm:text-base font-medium text-slate-600 dark:text-slate-400" data-id="element-241">
                  Über 1.500 zufriedene Kunden
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}